/* ─────────────────────────────────────────────────────
   BuildingMassMuscle.Com — Centralized SEO Configuration
   Provides metadata helpers for all pages + articles.
───────────────────────────────────────────────────── */

export const SITE = {
  name: "BuildingMassMuscle.Com",
  url: "https://www.buildingmassmuscle.com",
  twitterHandle: "@BuildingMassMuscle",
  defaultImage: "https://static.lumi.new/55/5561de71ecefd6401482618bcb750b8b.jpg",
  defaultDescription:
    "Build bigger muscles with science-backed training programs, nutrition guides, and transformation roadmaps. Expert advice for lean muscle building, female muscle growth, and ectomorph transformations.",
} as const;

/* ─── Type Definitions ─── */
export interface PageSeoMeta {
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
  ogType?: "website" | "article";
  publishDate?: string;
  author?: string;
  canonicalPath: string;
}

/* ─── Per-Page SEO Metadata ─── */
export const PAGE_SEO: Record<string, PageSeoMeta> = {
  home: {
    title: "BuildingMassMuscle.Com — Build Bigger Muscles & Lean Muscle Gains",
    description:
      "The #1 evidence-based resource for building bigger muscles. Lean muscle programs for men, female muscle growth guides, and ectomorph transformation plans. Start your journey today.",
    keywords:
      "bigger muscles, lean muscle, muscle mass, build muscle, hypertrophy, lean body for men, muscle gain",
    ogImage: SITE.defaultImage,
    ogType: "website",
    canonicalPath: "/",
  },
  articles: {
    title: "Muscle Building Articles — Training, Nutrition & Science | BuildingMassMuscle.Com",
    description:
      "Evidence-based articles on muscle building, nutrition, and body transformation. Guides covering leg workouts for mass, ectomorph muscle building, lean muscle definition, and more.",
    keywords:
      "muscle building articles, training guides, nutrition for muscle mass, bodyweight muscle gain, lean muscle definition, ectomorph muscle building",
    ogImage: SITE.defaultImage,
    ogType: "website",
    canonicalPath: "/articles",
  },
  calculator: {
    title: "Free Muscle Mass Calculator — Calories, Macros & Gain Projections | BuildingMassMuscle.Com",
    description:
      "Calculate your personalized muscle gain timeline, daily calorie targets, and macro breakdown. Free tool for beginners, ectomorphs, and advanced lifters.",
    keywords:
      "muscle mass calculator, muscle gain calculator, calorie calculator for muscle, macro calculator, ectomorph muscle calculator",
    ogImage: SITE.defaultImage,
    ogType: "website",
    canonicalPath: "/calculator",
  },
  leanPhysique: {
    title: "The Lean Physique Training Hub — Lean Body for Men | BuildingMassMuscle.Com",
    description:
      "Build a lean muscle body without excess fat. Structured programs for men targeting lean bulk strategies, body recomposition, and the aesthetic physique.",
    keywords:
      "lean body for men, lean muscle body, lean bulk, lean physique program, body recomposition men, lean muscle building",
    ogImage: "https://static.lumi.new/fd/fd6135de5e99578b6cc6eb193df53e1f.png",
    ogType: "website",
    canonicalPath: "/lean-physique",
  },
  womensStrength: {
    title: "Women's Strength Training Hub — Female Muscle Growth | BuildingMassMuscle.Com",
    description:
      "Science-backed strength training for women. Dispel the bulking myth, build lean muscle mass, and achieve the strong, athletic physique you want.",
    keywords:
      "female muscle growth, muscle mass for women, women strength training, women weight lifting, women muscle building, female hypertrophy",
    ogImage: "https://static.lumi.new/f3/f39d1189bb88cdc24adb4ce2e18f4682.jpg",
    ogType: "website",
    canonicalPath: "/womens-strength",
  },
  hardgainers: {
    title: "Hardgainers & Ectomorph Training Hub — Muscle Gain for Skinny Guys | BuildingMassMuscle.Com",
    description:
      "Ectomorph muscle building strategies that actually work. High-calorie nutrition tips, compound lifting programs, and realistic transformation timelines for hardgainers.",
    keywords:
      "ectomorph muscle building, muscle gain for skinny guys, hardgainer program, ectomorph diet, skinny to muscular, easiest muscle to build",
    ogImage: "https://static.lumi.new/cf/cf2eb6dd3b6fe7cb5cd6335fb522e0db.jpg",
    ogType: "website",
    canonicalPath: "/hardgainers",
  },
};

/* ─── Article SEO Data ─── */
export interface ArticleSeoData {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  image: string;
  publishDate: string;
  author: string;
  category: string;
}

export const ARTICLE_SEO: Record<string, ArticleSeoData> = {
  "leg-workout-for-mass": {
    slug: "leg-workout-for-mass",
    title: "The Ultimate Leg Workout for Mass — Build Powerful Legs | BuildingMassMuscle.Com",
    description:
      "A complete lower-body hypertrophy program targeting quads, hamstrings, and glutes. The quickest muscle to grow with progressive overload — squats, deadlifts, and isolation finishers.",
    keywords:
      "leg workout for mass, leg day hypertrophy, quad workout, hamstring workout, glute training, quickest muscle to grow, lower body muscle building",
    image: "https://static.lumi.new/a5/a5a8d088e8d760c41a992bfaf2b43d03.jpg",
    publishDate: "2024-01-15",
    author: "BuildingMassMuscle Team",
    category: "Training",
  },
  "10lb-muscle-gain-12-weeks": {
    slug: "10lb-muscle-gain-12-weeks",
    title: "How to Achieve a 10lb Muscle Gain in 12 Weeks | BuildingMassMuscle.Com",
    description:
      "A structured 12-week plan combining periodized training, caloric surplus strategies, and recovery optimization. Transform your physique with proven science-backed protocols.",
    keywords:
      "10lb muscle gain, 12 week muscle building program, muscle transformation, caloric surplus, muscle gain timeline, how to build muscle fast",
    image: "https://static.lumi.new/84/844be534ad37522e75870e1aeba903e6.png",
    publishDate: "2024-01-10",
    author: "BuildingMassMuscle Team",
    category: "Transformation",
  },
  "bodyweight-muscle-gain": {
    slug: "bodyweight-muscle-gain",
    title: "Bodyweight Muscle Gain Workouts for Home — No Gym Needed | BuildingMassMuscle.Com",
    description:
      "Build lean muscle at home with progressive bodyweight training. Time-under-tension protocols and volume techniques that drive real hypertrophy without equipment.",
    keywords:
      "bodyweight muscle gain, home workout muscle building, calisthenics muscle growth, bodyweight hypertrophy, build muscle at home, no gym workout",
    image: "https://static.lumi.new/c1/c14d05b80411c32049b4b44b86451ea8.png",
    publishDate: "2024-01-05",
    author: "BuildingMassMuscle Team",
    category: "Home Training",
  },
  "lean-muscle-definition": {
    slug: "lean-muscle-definition",
    title: "Lean Muscle Definition: The Science of Looking Ripped | BuildingMassMuscle.Com",
    description:
      "The science behind lean muscle definition — body fat percentage, muscle density, and water retention. How to achieve the ripped, defined look while preserving muscle mass.",
    keywords:
      "lean muscle definition, how to look ripped, muscle definition science, body fat percentage, lean muscle body, easiest muscle to build and define",
    image: "https://static.lumi.new/b9/b91b1fead38f767f5ddb056e35a21a5b.jpg",
    publishDate: "2024-01-01",
    author: "BuildingMassMuscle Team",
    category: "Science",
  },
  "nutrition-muscle-building": {
    slug: "nutrition-muscle-building",
    title: "Complete Nutrition Guide for Muscle Building — Macros & Meal Planning | BuildingMassMuscle.Com",
    description:
      "Master protein timing, carb cycling, and fat intake for optimal muscle growth. Evidence-based nutrition protocols covering macros for lean bulk and muscle mass.",
    keywords:
      "nutrition for muscle building, muscle building diet, protein for muscle growth, carb cycling muscle, macros for muscle gain, meal planning muscle",
    image: "https://static.lumi.new/ab/abf5f40c42ff8c828fc59a1f30f7e82c.jpg",
    publishDate: "2023-12-28",
    author: "BuildingMassMuscle Team",
    category: "Nutrition",
  },
  "lean-bulking-men": {
    slug: "lean-bulking-men",
    title: "Lean Bulking Guide for Men: Build Muscle Without Fat | BuildingMassMuscle.Com",
    description:
      "Strategic lean bulk techniques for men — maximize muscle gain while minimizing fat. The smart approach to a lean body for men with calorie targets and training structure.",
    keywords:
      "lean bulking guide, lean bulk for men, lean body for men, build muscle without fat, lean muscle body men, clean bulk vs dirty bulk",
    image: "https://static.lumi.new/9f/9f72830f1d2a5c23ec0a4bb81f54de6a.jpg",
    publishDate: "2023-12-20",
    author: "BuildingMassMuscle Team",
    category: "Training",
  },
  "women-strength-training": {
    slug: "women-strength-training",
    title: "Strength Training for Women: Myths Debunked — Female Muscle Growth | BuildingMassMuscle.Com",
    description:
      "Debunking women and weights misconceptions. How women can build lean muscle, increase strength, and transform body composition without getting bulky.",
    keywords:
      "strength training women, female muscle growth, women weightlifting myths, muscle mass for women, women body transformation, women hypertrophy",
    image: "https://static.lumi.new/c1/c102c9e1772d19390d288f61b935d110.jpg",
    publishDate: "2023-12-15",
    author: "BuildingMassMuscle Team",
    category: "Women's Training",
  },
  "progressive-overload-guide": {
    slug: "progressive-overload-guide",
    title: "Progressive Overload: The Key to Continuous Muscle Gains | BuildingMassMuscle.Com",
    description:
      "Understanding progressive overload — the fundamental principle behind muscle growth. How to systematically increase training stimulus for long-term hypertrophy.",
    keywords:
      "progressive overload, muscle growth principles, hypertrophy training, increase strength over time, muscle gain fundamentals, training progression",
    image: "https://static.lumi.new/55/5561de71ecefd6401482618bcb750b8b.jpg",
    publishDate: "2023-12-10",
    author: "BuildingMassMuscle Team",
    category: "Science",
  },
};

/* ─── Helper: build full page title ─── */
export function buildTitle(title: string): string {
  return title;
}

/* ─── Helper: build canonical URL ─── */
export function buildCanonical(path: string): string {
  return `${SITE.url}${path}`;
}

/* ─── Helper: build structured data (JSON-LD) for articles ─── */
export function buildArticleJsonLd(article: ArticleSeoData): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image,
    "author": {
      "@type": "Organization",
      "name": article.author,
      "url": SITE.url,
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE.name,
      "url": SITE.url,
    },
    "datePublished": article.publishDate,
    "dateModified": article.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": buildCanonical(`/articles/${article.slug}`),
    },
  });
}

/* ─── Helper: build JSON-LD for website ─── */
export function buildWebsiteJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE.name,
    "url": SITE.url,
    "description": SITE.defaultDescription,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE.url}/articles?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  });
}
